import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  FileText, Plus, Euro, Leaf, Heart, Package, MapPin,
  Building2, Calendar, Check, ChevronRight, Download, Eye,
  TrendingUp, Truck, CreditCard, Globe, ShieldCheck, Award,
  Sprout, Grape, Apple, Wheat, Flower2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes par catégorie
const ICONS_CATEGORIE = {
  ppam: Flower2,
  cereales: Wheat,
  vin: Grape,
  fruits: Apple,
  legumes: Sprout
};

// Badge certifications
const CertificationBadges = ({ contrat }) => (
  <div className="flex gap-1 flex-wrap">
    {contrat.certification_ffl && (
      <Badge className="bg-green-100 text-green-700 border-green-300">
        <Heart className="w-3 h-3 mr-1" /> Fair For Life
      </Badge>
    )}
    {contrat.certification_bio && (
      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
        <Leaf className="w-3 h-3 mr-1" /> Bio
      </Badge>
    )}
    {contrat.certification_hve && (
      <Badge className="bg-blue-100 text-blue-700 border-blue-300">
        <ShieldCheck className="w-3 h-3 mr-1" /> HVE
      </Badge>
    )}
  </div>
);

// Badge de statut
const StatutBadge = ({ statut }) => {
  const config = {
    brouillon: { label: 'Brouillon', class: 'bg-gray-100 text-gray-700' },
    actif: { label: 'Actif', class: 'bg-green-100 text-green-700' },
    en_cours: { label: 'En cours', class: 'bg-blue-100 text-blue-700' },
    termine: { label: 'Terminé', class: 'bg-purple-100 text-purple-700' },
    resilie: { label: 'Résilié', class: 'bg-red-100 text-red-700' }
  };
  const c = config[statut] || { label: statut, class: 'bg-gray-100' };
  return <Badge className={c.class}>{c.label}</Badge>;
};

// Carte de contrat
const ContratCard = ({ contrat, onVoir }) => {
  const IconCategorie = ICONS_CATEGORIE[contrat.produits?.[0]?.categorie] || Package;
  
  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-green-500"
      onClick={() => onVoir(contrat)}
      data-testid={`contrat-card-${contrat.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs">{contrat.numero}</Badge>
              <StatutBadge statut={contrat.statut} />
            </div>
            <h3 className="font-semibold text-lg">{contrat.client_nom}</h3>
            <p className="text-sm text-muted-foreground">
              {contrat.type_contrat} • Campagne {contrat.annee_campagne}
            </p>
            <CertificationBadges contrat={contrat} />
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-primary">
              <IconCategorie className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-primary">
              {contrat.montant_total_prevu?.toLocaleString('fr-FR')} €
            </p>
            <p className="text-xs text-muted-foreground">CA prévu</p>
          </div>
        </div>

        {/* Produits */}
        <div className="flex flex-wrap gap-1 mb-3">
          {contrat.produits?.slice(0, 3).map((p, i) => (
            <Badge key={i} variant="secondary" className="text-xs">
              {p.libelle}
            </Badge>
          ))}
          {contrat.produits?.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{contrat.produits.length - 3}
            </Badge>
          )}
        </div>

        {/* Infos */}
        <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {contrat.surface_reservee || 0} ha
          </div>
          <div className="flex items-center gap-1">
            <Euro className="w-3 h-3" />
            Facturé: {(contrat.montant_facture || 0).toLocaleString('fr-FR')}€
          </div>
          {contrat.total_prefinancement > 0 && (
            <div className="flex items-center gap-1 text-green-600">
              <CreditCard className="w-3 h-3" />
              Préfin: {contrat.total_prefinancement?.toLocaleString('fr-FR')}€
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Modal création de contrat agricole
const NouveauContratModal = ({ open, onClose, clients, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [typesProduits, setTypesProduits] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  
  const [form, setForm] = useState({
    // Client
    client_id: '',
    client_nom: '',
    
    // Type
    type_contrat: 'annuel',
    certification_ffl: false,
    certification_bio: false,
    certification_hve: false,
    
    // Période
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: `${new Date().getFullYear()}-12-31`,
    annee_campagne: new Date().getFullYear(),
    nb_annees: 1,
    tacite_reconduction: true,
    
    // Produits
    produits: [],
    
    // Parcelles
    parcelles_ids: [],
    surface_reservee: 0,
    
    // Prix
    prix_minimum_garanti: null,
    bonus_qualite: 0,
    
    // Préfinancement
    prefinancements: [],
    
    // Paiement
    delai_paiement: 30,
    acompte_campagne: 0,
    
    // FFL
    clause_tracabilite: true,
    clause_transparence_prix: true,
    prime_developpement: 0,
    
    notes: ''
  });

  useEffect(() => {
    if (open) {
      loadTypesProduits();
      loadParcelles();
    }
  }, [open]);

  const loadTypesProduits = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contrats-agricoles/types-produits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTypesProduits(data.produits || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadParcelles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/parcellaire/parcelles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setParcelles(data.parcelles || data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const selectClient = (client) => {
    setForm({
      ...form,
      client_id: client.id,
      client_nom: client.nom || client.entreprise
    });
  };

  const ajouterProduit = (produit) => {
    const existe = form.produits.find(p => p.code === produit.code);
    if (!existe) {
      setForm({
        ...form,
        produits: [...form.produits, {
          code: produit.code,
          libelle: produit.libelle,
          categorie: produit.categorie,
          volume_prevu: 100,
          unite: produit.unite,
          prix_unitaire: produit.prix_defaut,
          prime_ffl: form.certification_ffl ? 5 : 0,
          qualite: form.certification_bio ? 'Bio' : 'Conventionnel'
        }]
      });
    }
  };

  const updateProduit = (index, field, value) => {
    const updated = [...form.produits];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, produits: updated });
  };

  const supprimerProduit = (index) => {
    setForm({
      ...form,
      produits: form.produits.filter((_, i) => i !== index)
    });
  };

  const toggleParcelle = (parcelle) => {
    const existe = form.parcelles_ids.includes(parcelle.id);
    if (existe) {
      setForm({
        ...form,
        parcelles_ids: form.parcelles_ids.filter(id => id !== parcelle.id),
        surface_reservee: form.surface_reservee - (parcelle.surface || 0)
      });
    } else {
      setForm({
        ...form,
        parcelles_ids: [...form.parcelles_ids, parcelle.id],
        surface_reservee: form.surface_reservee + (parcelle.surface || 0)
      });
    }
  };

  const ajouterPrefinancement = () => {
    setForm({
      ...form,
      prefinancements: [...form.prefinancements, {
        type: 'plantation',
        description: '',
        montant_ht: 0,
        duree_amortissement: 3,
        mode_recuperation: 'deduction_facture'
      }]
    });
  };

  const updatePrefinancement = (index, field, value) => {
    const updated = [...form.prefinancements];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, prefinancements: updated });
  };

  const supprimerPrefinancement = (index) => {
    setForm({
      ...form,
      prefinancements: form.prefinancements.filter((_, i) => i !== index)
    });
  };

  const calculerTotal = () => {
    return form.produits.reduce((sum, p) => 
      sum + (p.volume_prevu * (p.prix_unitaire + (p.prime_ffl || 0))), 0
    );
  };

  const calculerTotalPrefinancement = () => {
    return form.prefinancements.reduce((sum, pf) => sum + (pf.montant_ht || 0), 0);
  };

  const handleSubmit = async () => {
    if (!form.client_id || form.produits.length === 0) {
      toast.error('Sélectionnez un client et ajoutez des produits');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contrats-agricoles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Contrat ${data.numero} créé avec succès`);
        onSuccess?.();
        onClose();
        resetForm();
      } else {
        throw new Error('Erreur création');
      }
    } catch (err) {
      toast.error('Erreur lors de la création du contrat');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      client_id: '', client_nom: '',
      type_contrat: 'annuel',
      certification_ffl: false, certification_bio: false, certification_hve: false,
      date_debut: new Date().toISOString().split('T')[0],
      date_fin: `${new Date().getFullYear()}-12-31`,
      annee_campagne: new Date().getFullYear(),
      nb_annees: 1, tacite_reconduction: true,
      produits: [], parcelles_ids: [], surface_reservee: 0,
      prix_minimum_garanti: null, bonus_qualite: 0,
      prefinancements: [],
      delai_paiement: 30, acompte_campagne: 0,
      clause_tracabilite: true, clause_transparence_prix: true, prime_developpement: 0,
      notes: ''
    });
    setStep(1);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-600" />
            Nouveau Contrat Agricole
          </DialogTitle>
          <DialogDescription>
            Étape {step}/5 - {['Client & Type', 'Produits', 'Parcelles', 'Préfinancement', 'Validation'][step-1]}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map(s => (
            <div
              key={s}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step >= s ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
          ))}
        </div>

        {/* Étape 1: Client & Type */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Client et type de contrat</h3>
            
            {/* Sélection client */}
            <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
              {clients.map(client => (
                <button
                  key={client.id}
                  onClick={() => selectClient(client)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    form.client_id === client.id 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-muted hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold">{client.nom || client.entreprise}</p>
                      <p className="text-xs text-muted-foreground">{client.commune}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Type de contrat */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de contrat</Label>
                <Select value={form.type_contrat} onValueChange={v => setForm({...form, type_contrat: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vente_directe">Vente directe</SelectItem>
                    <SelectItem value="annuel">Contrat annuel</SelectItem>
                    <SelectItem value="pluriannuel">Pluriannuel</SelectItem>
                    <SelectItem value="ffl">Fair For Life</SelectItem>
                    <SelectItem value="tripartite">Tripartite</SelectItem>
                    <SelectItem value="prefinancement">Avec préfinancement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Campagne</Label>
                <Input 
                  type="number"
                  value={form.annee_campagne}
                  onChange={e => setForm({...form, annee_campagne: parseInt(e.target.value)})}
                />
              </div>
            </div>

            {/* Certifications */}
            <div className="p-4 bg-green-50 rounded-lg">
              <Label className="mb-2 block">Certifications</Label>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="ffl"
                    checked={form.certification_ffl}
                    onCheckedChange={c => setForm({...form, certification_ffl: c})}
                  />
                  <Label htmlFor="ffl" className="flex items-center gap-1 cursor-pointer">
                    <Heart className="w-4 h-4 text-green-600" /> Fair For Life
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="bio"
                    checked={form.certification_bio}
                    onCheckedChange={c => setForm({...form, certification_bio: c})}
                  />
                  <Label htmlFor="bio" className="flex items-center gap-1 cursor-pointer">
                    <Leaf className="w-4 h-4 text-emerald-600" /> Agriculture Bio
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="hve"
                    checked={form.certification_hve}
                    onCheckedChange={c => setForm({...form, certification_hve: c})}
                  />
                  <Label htmlFor="hve" className="flex items-center gap-1 cursor-pointer">
                    <ShieldCheck className="w-4 h-4 text-blue-600" /> HVE
                  </Label>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date début</Label>
                <Input 
                  type="date"
                  value={form.date_debut}
                  onChange={e => setForm({...form, date_debut: e.target.value})}
                />
              </div>
              <div>
                <Label>Date fin</Label>
                <Input 
                  type="date"
                  value={form.date_fin}
                  onChange={e => setForm({...form, date_fin: e.target.value})}
                />
              </div>
            </div>
          </div>
        )}

        {/* Étape 2: Produits */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Produits contractualisés</h3>

            {/* Produits ajoutés */}
            {form.produits.length > 0 && (
              <div className="space-y-2 mb-4">
                {form.produits.map((p, idx) => {
                  const IconCat = ICONS_CATEGORIE[p.categorie] || Package;
                  return (
                    <Card key={idx} className="bg-green-50/50">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <IconCat className="w-5 h-5 text-green-600" />
                            <span className="font-medium">{p.libelle}</span>
                            <Badge variant="outline" className="text-xs">{p.qualite}</Badge>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 h-6 w-6 p-0"
                            onClick={() => supprimerProduit(idx)}
                          >
                            ×
                          </Button>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              value={p.volume_prevu}
                              onChange={e => updateProduit(idx, 'volume_prevu', parseFloat(e.target.value))}
                              className="w-20 h-8"
                            />
                            <span className="text-sm text-muted-foreground">{p.unite}</span>
                          </div>
                          <span className="text-muted-foreground">×</span>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              value={p.prix_unitaire}
                              onChange={e => updateProduit(idx, 'prix_unitaire', parseFloat(e.target.value))}
                              className="w-20 h-8"
                            />
                            <span className="text-sm text-muted-foreground">€</span>
                          </div>
                          {form.certification_ffl && (
                            <>
                              <span className="text-muted-foreground">+</span>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  value={p.prime_ffl}
                                  onChange={e => updateProduit(idx, 'prime_ffl', parseFloat(e.target.value))}
                                  className="w-16 h-8"
                                />
                                <span className="text-sm text-green-600">€ FFL</span>
                              </div>
                            </>
                          )}
                          <span className="ml-auto font-bold">
                            {(p.volume_prevu * (p.prix_unitaire + (p.prime_ffl || 0))).toLocaleString('fr-FR')} €
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                <div className="flex justify-end p-2 bg-green-100 rounded-lg">
                  <span className="font-bold text-lg">
                    Total: {calculerTotal().toLocaleString('fr-FR')} € HT
                  </span>
                </div>
              </div>
            )}

            {/* Catalogue produits */}
            <div className="border rounded-lg p-3 max-h-60 overflow-y-auto">
              <p className="text-sm text-muted-foreground mb-2">Cliquez pour ajouter:</p>
              
              {['ppam', 'cereales', 'vin', 'fruits', 'legumes'].map(cat => {
                const produitsCategorie = typesProduits.filter(p => p.categorie === cat);
                if (produitsCategorie.length === 0) return null;
                
                const IconCat = ICONS_CATEGORIE[cat] || Package;
                return (
                  <div key={cat} className="mb-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase flex items-center gap-1 mb-1">
                      <IconCat className="w-3 h-3" /> {cat}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {produitsCategorie.map(p => (
                        <button
                          key={p.code}
                          onClick={() => ajouterProduit(p)}
                          disabled={form.produits.some(fp => fp.code === p.code)}
                          className={`px-2 py-1 text-xs rounded-lg border transition-all ${
                            form.produits.some(fp => fp.code === p.code)
                              ? 'bg-gray-100 text-gray-400'
                              : 'hover:border-green-500 hover:bg-green-50'
                          }`}
                        >
                          {p.libelle}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Étape 3: Parcelles */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Parcelles réservées</h3>
            <p className="text-sm text-muted-foreground">
              Sélectionnez les parcelles dédiées à ce contrat. Surface sélectionnée: <strong>{form.surface_reservee.toFixed(2)} ha</strong>
            </p>

            <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {parcelles.map(parcelle => (
                <button
                  key={parcelle.id}
                  onClick={() => toggleParcelle(parcelle)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    form.parcelles_ids.includes(parcelle.id)
                      ? 'border-green-500 bg-green-50'
                      : 'border-muted hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{parcelle.nom}</p>
                      <p className="text-xs text-muted-foreground">
                        {parcelle.culture} • {parcelle.surface?.toFixed(2) || 0} ha
                      </p>
                    </div>
                    {form.parcelles_ids.includes(parcelle.id) && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Étape 4: Préfinancement */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Préfinancement (optionnel)</h3>
            <p className="text-sm text-muted-foreground">
              L'acheteur peut préfinancer des investissements (plantation, irrigation...) qui seront déduits automatiquement sur les factures.
            </p>

            {form.prefinancements.map((pf, idx) => (
              <Card key={idx}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <Label>Préfinancement {idx + 1}</Label>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-500"
                      onClick={() => supprimerPrefinancement(idx)}
                    >
                      Supprimer
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select 
                        value={pf.type} 
                        onValueChange={v => updatePrefinancement(idx, 'type', v)}
                      >
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="plantation">Plantation</SelectItem>
                          <SelectItem value="plants">Plants / Semences</SelectItem>
                          <SelectItem value="irrigation">Irrigation</SelectItem>
                          <SelectItem value="infrastructure">Infrastructure</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Montant HT (€)</Label>
                      <Input
                        type="number"
                        value={pf.montant_ht}
                        onChange={e => updatePrefinancement(idx, 'montant_ht', parseFloat(e.target.value))}
                        className="h-9"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={pf.description}
                      onChange={e => updatePrefinancement(idx, 'description', e.target.value)}
                      placeholder="Ex: Plantation de 2ha de lavande"
                      className="h-9"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Durée amortissement (années)</Label>
                      <Select 
                        value={String(pf.duree_amortissement)} 
                        onValueChange={v => updatePrefinancement(idx, 'duree_amortissement', parseInt(v))}
                      >
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 an</SelectItem>
                          <SelectItem value="2">2 ans</SelectItem>
                          <SelectItem value="3">3 ans</SelectItem>
                          <SelectItem value="4">4 ans</SelectItem>
                          <SelectItem value="5">5 ans</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <p className="text-sm text-muted-foreground">
                        → {(pf.montant_ht / pf.duree_amortissement).toFixed(2)} €/an déduits
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button variant="outline" onClick={ajouterPrefinancement} className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un préfinancement
            </Button>

            {form.prefinancements.length > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="font-medium">Total préfinancement: {calculerTotalPrefinancement().toLocaleString('fr-FR')} € HT</p>
                <p className="text-sm text-muted-foreground">
                  Ce montant sera déduit progressivement sur vos factures.
                </p>
              </div>
            )}

            {/* Conditions de paiement */}
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-3">Conditions de paiement</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Délai de paiement (jours)</Label>
                  <Select 
                    value={String(form.delai_paiement)} 
                    onValueChange={v => setForm({...form, delai_paiement: parseInt(v)})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Comptant</SelectItem>
                      <SelectItem value="30">30 jours</SelectItem>
                      <SelectItem value="45">45 jours</SelectItem>
                      <SelectItem value="60">60 jours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Acompte campagne (%)</Label>
                  <Input
                    type="number"
                    value={form.acompte_campagne}
                    onChange={e => setForm({...form, acompte_campagne: parseFloat(e.target.value)})}
                    min="0"
                    max="50"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Étape 5: Validation */}
        {step === 5 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Récapitulatif du contrat</h3>

            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Client et certifications */}
                <div className="flex items-center justify-between pb-3 border-b">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-10 h-10 text-green-600" />
                    <div>
                      <p className="font-bold text-lg">{form.client_nom}</p>
                      <p className="text-sm text-muted-foreground">
                        {form.type_contrat} • Campagne {form.annee_campagne}
                      </p>
                    </div>
                  </div>
                  <CertificationBadges contrat={form} />
                </div>

                {/* Produits */}
                <div>
                  <p className="font-medium mb-2">Produits ({form.produits.length})</p>
                  <div className="space-y-1 text-sm">
                    {form.produits.map((p, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{p.libelle}</span>
                        <span>
                          {p.volume_prevu} {p.unite} × {p.prix_unitaire}€ 
                          {p.prime_ffl > 0 && <span className="text-green-600"> (+{p.prime_ffl}€ FFL)</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Parcelles */}
                {form.parcelles_ids.length > 0 && (
                  <div>
                    <p className="font-medium mb-1">Parcelles réservées</p>
                    <p className="text-sm text-muted-foreground">
                      {form.parcelles_ids.length} parcelle(s) • {form.surface_reservee.toFixed(2)} ha
                    </p>
                  </div>
                )}

                {/* Préfinancement */}
                {form.prefinancements.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="font-medium">Préfinancement</p>
                    <p className="text-sm">
                      {calculerTotalPrefinancement().toLocaleString('fr-FR')} € HT sur {form.prefinancements.length} investissement(s)
                    </p>
                  </div>
                )}

                {/* Total */}
                <div className="bg-green-100 p-3 rounded-lg">
                  <div className="flex justify-between text-lg font-bold">
                    <span>CA PRÉVISIONNEL</span>
                    <span>{calculerTotal().toLocaleString('fr-FR')} € HT</span>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes (optionnel)</Label>
                  <Textarea
                    value={form.notes}
                    onChange={e => setForm({...form, notes: e.target.value})}
                    placeholder="Conditions particulières, remarques..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
          >
            {step > 1 ? 'Retour' : 'Annuler'}
          </Button>

          {step < 5 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !form.client_id) || 
                (step === 2 && form.produits.length === 0)
              }
              className="bg-green-600 hover:bg-green-700"
            >
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Création...' : 'Créer le contrat'}
              <Check className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== PAGE PRINCIPALE ====================

const ContratsAgricolesPage = () => {
  const [contrats, setContrats] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewContrat, setShowNewContrat] = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [filtreFFL, setFiltreFFL] = useState(false);
  const [annee, setAnnee] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, [annee, filtreFFL]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      let url = `${API_URL}/api/contrats-agricoles?annee=${annee}`;
      if (filtreFFL) url += `&ffl_only=true`;
      
      const [contratsRes, statsRes, clientsRes] = await Promise.all([
        fetch(url, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/contrats-agricoles/stats/rentabilite?annee=${annee}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/crm/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (contratsRes.ok) {
        const data = await contratsRes.json();
        setContrats(data.contrats || []);
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const voirContrat = async (contrat) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contrats-agricoles/${contrat.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedContrat(data);
        setShowDetail(true);
      }
    } catch (err) {
      toast.error('Erreur chargement contrat');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="contrats-agricoles-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            Contrats Agricoles
          </h1>
          <p className="text-muted-foreground mt-1">
            Commercialisation, Fair For Life, Préfinancement
          </p>
        </div>
        
        <Button 
          size="lg"
          className="gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
          onClick={() => setShowNewContrat(true)}
          data-testid="new-contrat-agricole-btn"
        >
          <Plus className="w-5 h-5" />
          Nouveau contrat
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.nb_contrats}</p>
                  <p className="text-xs text-muted-foreground">Contrats {annee}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.ca_previsionnel?.toLocaleString('fr-FR')}€</p>
                  <p className="text-xs text-muted-foreground">CA prévu</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.contrats_ffl?.nb || 0}</p>
                  <p className="text-xs text-muted-foreground">Fair For Life</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_prefinancement?.toLocaleString('fr-FR')}€</p>
                  <p className="text-xs text-muted-foreground">Préfinancements</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.surface_reservee?.toFixed(1)} ha</p>
                  <p className="text-xs text-muted-foreground">Réservés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Label>Année:</Label>
          <Select value={String(annee)} onValueChange={v => setAnnee(parseInt(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Checkbox 
            id="ffl-filter"
            checked={filtreFFL}
            onCheckedChange={setFiltreFFL}
          />
          <Label htmlFor="ffl-filter" className="flex items-center gap-1 cursor-pointer">
            <Heart className="w-4 h-4 text-green-600" /> Fair For Life uniquement
          </Label>
        </div>
      </div>

      {/* Liste des contrats */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      ) : contrats.length === 0 ? (
        <Card className="p-12 text-center">
          <Leaf className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Aucun contrat</h3>
          <p className="text-muted-foreground mb-4">Créez votre premier contrat de commercialisation</p>
          <Button onClick={() => setShowNewContrat(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> Nouveau contrat
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {contrats.map(c => (
            <ContratCard key={c.id} contrat={c} onVoir={voirContrat} />
          ))}
        </div>
      )}

      {/* Modal nouveau contrat */}
      <NouveauContratModal 
        open={showNewContrat}
        onClose={() => setShowNewContrat(false)}
        clients={clients}
        onSuccess={loadData}
      />

      {/* Modal détail contrat */}
      {selectedContrat && (
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Badge variant="outline">{selectedContrat.contrat?.numero}</Badge>
                {selectedContrat.contrat?.client_nom}
              </DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="detail">
              <TabsList>
                <TabsTrigger value="detail">Détails</TabsTrigger>
                <TabsTrigger value="livraisons">Livraisons</TabsTrigger>
                <TabsTrigger value="rentabilite">Rentabilité</TabsTrigger>
                <TabsTrigger value="document">Document</TabsTrigger>
              </TabsList>

              <TabsContent value="detail" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Informations</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type:</span>
                        <span>{selectedContrat.contrat?.type_contrat}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Période:</span>
                        <span>{selectedContrat.contrat?.date_debut} → {selectedContrat.contrat?.date_fin}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Certifications:</span>
                        <CertificationBadges contrat={selectedContrat.contrat || {}} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Montants</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">CA prévu:</span>
                        <span className="font-bold">{selectedContrat.contrat?.montant_total_prevu?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Facturé:</span>
                        <span>{selectedContrat.contrat?.montant_facture?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Préfinancement:</span>
                        <span className="text-green-600">{selectedContrat.contrat?.total_prefinancement?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Reste à déduire:</span>
                        <span>{selectedContrat.contrat?.prefinancement_restant?.toLocaleString('fr-FR')}€</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Parcelles */}
                {selectedContrat.parcelles?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Parcelles réservées
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedContrat.parcelles.map((p, i) => (
                          <Badge key={i} variant="outline">
                            {p.nom} ({p.surface?.toFixed(2)} ha)
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="livraisons" className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Avancement des livraisons</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                      <Progress value={selectedContrat.avancement?.taux_global || 0} className="flex-1" />
                      <span className="font-bold">{selectedContrat.avancement?.taux_global}%</span>
                    </div>
                    
                    <div className="space-y-3">
                      {selectedContrat.avancement?.produits?.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>{p.libelle}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">
                              {p.volume_livre}/{p.volume_prevu}
                            </span>
                            <Progress value={p.taux_livraison} className="w-24 h-2" />
                            <span className="w-12 text-right">{p.taux_livraison}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="rentabilite">
                {selectedContrat.rentabilite && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Rentabilité prévisionnelle
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>CA prévisionnel:</span>
                        <span className="font-bold">{selectedContrat.rentabilite.ca_previsionnel?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Préfinancement reçu:</span>
                        <span className="text-green-600">+{selectedContrat.rentabilite.total_prefinancement?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Déduction annuelle:</span>
                        <span className="text-amber-600">-{selectedContrat.rentabilite.amortissement_annuel?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-bold">
                        <span>CA net après déduction:</span>
                        <span>{selectedContrat.rentabilite.ca_net_apres_deduction?.toLocaleString('fr-FR')}€</span>
                      </div>
                      {selectedContrat.rentabilite.rendement_par_ha > 0 && (
                        <div className="flex justify-between text-muted-foreground">
                          <span>Rendement par hectare:</span>
                          <span>{selectedContrat.rentabilite.rendement_par_ha?.toLocaleString('fr-FR')}€/ha</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="document">
                <Card>
                  <CardContent className="p-4">
                    <Button variant="outline" className="mb-4">
                      <Download className="w-4 h-4 mr-2" /> Télécharger le contrat
                    </Button>
                    <pre className="text-xs bg-muted p-4 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap">
                      {selectedContrat.contrat?.document_texte || 'Document non généré'}
                    </pre>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ContratsAgricolesPage;
